import {
  buildSupportMessage,
  SUPPORT_SUMMARY_MAX,
  SUPPORT_SUMMARY_MIN,
  SUPPORT_TOPICS,
  supportIconOf,
  supportSenderOf,
} from '../../assistant/assistant-support';
import type { TSupportTopic } from '../../assistant/assistant-support';
import { cardTextLength } from '../../cards/card-text';
import { toolCardId } from '../../cards/card-ids';
import { supportCard } from '../../cards/support-card';
import type { IHostToolDefinition } from '../../gpthelp-host-contract';
import { getOptionalEnum, getRequiredString } from '../tool-args';
import { toolResultEnvelope } from '../tool-envelope';
import { HostToolError } from '../tool-types';
import type { IGpthelpTool } from '../tool-types';

/** What the model learns: the card is shown and sends nothing by itself. */
const SUPPORT_SHOWN_NOTE =
  'Nothing is sent until the user taps the card. One short sentence.';

/**
 * Requests only the member may make in their own words: after customer
 * text, a «change the owner's email» could come from a customer's note.
 */
const OWN_WORDS_TOPICS: readonly TSupportTopic[] = ['account', 'login'];

/**
 * What the model learns when such a request comes in a conversation that
 * read customer text. The taint lasts the rest of the conversation
 * (gpthelp-api stores it), so asking again there never helps: the way out
 * is a new conversation or the ⋯ menu's «Hablar con una persona», which is
 * there whenever this tool is (both need the store's support link).
 */
const OWN_WORDS_REFUSAL =
  'This conversation includes text written by customers, so account and login requests are not prepared here. Tell the user to start a new conversation and type the request there, or to use the talk-to-a-person option in the ⋯ menu.';

const SUPPORT_HANDOFF_DEFINITION: IHostToolDefinition = {
  name: 'support_handoff',
  description:
    "Shows a card that opens a message to our support team with the request already written; the user sends it. Use it for what only the team can do: account or login problems, changing the owner or the login email, closing or deleting the store or the account, plan or billing problems, a bug you cannot solve. Never for things the user can change in the panel (show that page instead). Passwords are not support: the user logs out and uses 'Forgot my password'. Nothing is sent until the user taps.",
  sideEffects: 'none',
  parameters: {
    type: 'object',
    additionalProperties: false,
    required: ['topic', 'summary'],
    properties: {
      topic: { type: 'string', enum: [...SUPPORT_TOPICS] },
      summary: {
        type: 'string',
        minLength: SUPPORT_SUMMARY_MIN,
        maxLength: SUPPORT_SUMMARY_MAX,
        description:
          "The user's request in one or two sentences, in the user's words. Never include passwords, codes or card numbers.",
      },
    },
  },
};

function topicArg(args: Record<string, unknown>): TSupportTopic {
  const topic = getOptionalEnum(args, 'topic', SUPPORT_TOPICS);
  if (!topic) {
    throw new HostToolError('INVALID_ARGS', 'Missing "topic"', {
      key: 'topic',
      value: args.topic ?? null,
      allowed: SUPPORT_TOPICS,
    });
  }
  return topic;
}

/** The request; a longer one than the schema allows is cut by the message builder. */
function summaryArg(args: Record<string, unknown>): string {
  const summary = getRequiredString(args, 'summary');
  if (cardTextLength(summary) < SUPPORT_SUMMARY_MIN) {
    throw new HostToolError('INVALID_ARGS', 'The "summary" is too short', {
      key: 'summary',
      value: summary,
      minLength: SUPPORT_SUMMARY_MIN,
    });
  }
  return summary;
}

/**
 * `support_handoff` (plan §8 R4): a card with the message to our support
 * team, written once here from the member's own name, the store and the
 * request, and a button that opens it in WhatsApp or email to send. In a
 * conversation that read customer text, account and login requests are
 * refused and any other card says it is based on a customer's text.
 */
export const supportHandoffTool: IGpthelpTool = {
  definition: SUPPORT_HANDOFF_DEFINITION,
  policy: 'ui',
  isAvailable: (gate) => gate.hasRestaurantScope && gate.supportUrl !== null,
  execute(args, { deps, scope, gate, turn, fmt }) {
    const supportUrl = gate.supportUrl;
    if (!supportUrl) {
      throw new HostToolError(
        'NOT_AVAILABLE',
        'This store has no support link.'
      );
    }
    const topic = topicArg(args);
    if (turn.tainted && OWN_WORDS_TOPICS.includes(topic)) {
      throw new HostToolError('FORBIDDEN', OWN_WORDS_REFUSAL, { topic });
    }
    const { summary, message } = buildSupportMessage({
      fmt,
      sender: supportSenderOf(deps),
      topic,
      summary: summaryArg(args),
      supportUrl,
    });
    return toolResultEnvelope({
      data: { shown: true, note: SUPPORT_SHOWN_NOTE },
      cards: [
        supportCard(
          {
            id: toolCardId('support', turn.requestId),
            topic,
            summary,
            message,
            icon: supportIconOf(supportUrl),
            restaurantId: scope.restaurantId,
            branchId: scope.branchId,
            locale: fmt.locale,
            tainted: turn.tainted,
          },
          fmt
        ),
      ],
    });
  },
};

/** The support handoff (registered right after `ui_navigate`, plan F11). */
export const supportTools: IGpthelpTool[] = [supportHandoffTool];
